/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  useMemo,
  useRef,
  type ReactNode,
  type MouseEvent,
  type FocusEventHandler,
  useCallback,
} from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import ScreenLink from 'components/common/ScreenLink';
import type { IScreenLinkData } from 'components/common/ScreenLink/ScreenLink';
import Button from 'components/common/Button';
import { scrollToNextMatchingSibling } from 'utils/form';
import { useAppContext } from 'contexts/AppContext';
import { useIntl, type IntlShape } from 'react-intl';
import type { TFieldComponentProps } from './types';
import styles from './ButtonSelect.module.scss';

type TFormatMessage = IntlShape['formatMessage'];

export type TButtonSelectValue = string | number | boolean;

type TButtonSelectFieldValue = TButtonSelectValue | TButtonSelectValue[] | null | undefined;

/** What a click sets: the option value, null (deselected) or the values of a multi select. */
export type TButtonSelectChange =
  TButtonSelectValue | Array<TButtonSelectValue | null | undefined> | null;

type TButtonSelectIconRenderer = (args: { altText: string }) => ReactNode;

/** An icon element, or a function rendering it with the translated alt text. */
export type TButtonSelectIcon = ReactNode | TButtonSelectIconRenderer;

export interface IButtonSelectOption {
  value: TButtonSelectValue;
  label?: string | number;
  altText?: string;
  topIcon?: TButtonSelectIcon;
  startIcon?: TButtonSelectIcon;
  endIcon?: TButtonSelectIcon;
}

interface IGetIconArgs {
  icon: TButtonSelectIcon;
  altText?: string;
  label?: string | number;
  t: TFormatMessage;
}

interface IGetIconWithAltTextArgs {
  icon: TButtonSelectIconRenderer;
  altText?: string | number;
  t: TFormatMessage;
}

interface ISButtonProps {
  name: string;
  value: TButtonSelectFieldValue;
  option: IButtonSelectOption;
  handleToggle: (option: IButtonSelectOption, e: MouseEvent<HTMLButtonElement>) => void;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
  theme: string;
  disabled: boolean;
}

interface IButtonSelectOwnProps {
  label?: ReactNode;
  options: IButtonSelectOption[];
  multi?: boolean;
  nullable?: boolean;
  description?: ReactNode;
  moreDetails?: IScreenLinkData;
  inOneRow?: boolean;
  skipScrollingToField?: boolean;
  classnames?: string;
  disabled?: boolean;
  largeLabel?: boolean;
}

interface IInlineSelectProps extends IButtonSelectOwnProps {
  onChange: (value: TButtonSelectChange) => Promise<void>;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
  value: TButtonSelectFieldValue;
  error?: ReactNode;
  theme: string;
  name: string;
}

export interface IButtonSelectProps
  extends TFieldComponentProps<TButtonSelectFieldValue>, IButtonSelectOwnProps {
  sideEffectOnButtonClick?: (name: string, value: TButtonSelectChange) => void | Promise<void>;
}

const isIconRenderer = (icon: TButtonSelectIcon): icon is TButtonSelectIconRenderer =>
  typeof icon === 'function';

// The click target is the button or an element in it (label, icon), all of which can blur.
const blurTarget = (target: EventTarget) => {
  if (target instanceof HTMLElement || target instanceof SVGElement) {
    target.blur();
  } else {
    throw new TypeError('e.target.blur is not a function');
  }
};

function getIconWithAltText({ altText, icon, t }: IGetIconWithAltTextArgs) {
  return icon({ altText: t({ id: 'alt_text.btn_select_with_icon' }, { btnLabel: altText }) });
}

function getIcon({ icon, altText, label, t }: IGetIconArgs) {
  if (isIconRenderer(icon)) {
    return getIconWithAltText({ icon, altText: altText || label, t });
  }
  return icon;
}

const SButton = ({ name, value, option, handleToggle, onBlur, theme, disabled }: ISButtonProps) => {
  const { formatMessage: t } = useIntl();
  const { topIcon, endIcon, startIcon, label, altText } = option;

  const themeToButtonColor: Record<string, Record<'selected' | 'notSelected', string>> = {
    primary: {
      selected: styles.selected,
      notSelected: styles.notSelected,
    },
    green: {
      selected: styles.greenSelected,
      notSelected: styles.greenNotSelected,
    },
  };

  const isSelected = useCallback(
    (o: IButtonSelectOption) =>
      Array.isArray(value) ? value.includes(o.value) : o.value === value,
    [value],
  );

  return (
    <Button
      name={name}
      // React reads the key as a string ('' + key).
      key={String(option.value)}
      onClick={(e) => handleToggle(option, e)}
      onBlur={onBlur}
      disabled={disabled}
      className={cn(
        styles.button,
        topIcon ? styles.btnContentInColumn : '',
        startIcon ? styles.btnWithStartIcon : '',
        isSelected(option)
          ? themeToButtonColor[theme].selected
          : themeToButtonColor[theme].notSelected,
      )}
      startIcon={getIcon({ icon: startIcon, t, label, altText })}
      endIcon={getIcon({ icon: endIcon, t, label, altText })}
      topIcon={getIcon({ icon: topIcon, t, label, altText })}
    >
      {label}
    </Button>
  );
};

const InlineSelect = ({
  label,
  onChange,
  onBlur,
  value,
  options,
  multi,
  nullable,
  error,
  description,
  moreDetails,
  inOneRow = true,
  skipScrollingToField,
  theme,
  classnames,
  disabled = false,
  name,
  largeLabel,
}: IInlineSelectProps) => {
  const fieldWrapperRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(
    async (option: IButtonSelectOption, e: MouseEvent<HTMLButtonElement>) => {
      async function triggerChange() {
        if (multi) {
          let result: Array<TButtonSelectValue | null | undefined> | null = null;
          if (Array.isArray(value)) {
            result = value;
            if (value.includes(option.value))
              result = result.filter((item) => item !== option.value);
            else result = [...result, option.value];
          } else {
            // Bug: without an array value the first click sets `[value]` (e.g. `[undefined]`)
            // instead of selecting the option; the next click adds it.
            result = [value];
          }
          await onChange(result);
        } else if (value === option.value) {
          if (nullable !== false) {
            await onChange(null);
          }
        } else {
          await onChange(option.value);
        }
      }
      e.persist();
      await triggerChange();
      blurTarget(e.target);
      if (!multi && !skipScrollingToField) {
        scrollToNextMatchingSibling(fieldWrapperRef);
      }
    },
    [multi, value, onChange, nullable, skipScrollingToField],
  );

  const fieldWrapperClassname = useMemo(() => {
    let classes = 'field-wrapper';
    if (largeLabel) {
      classes += ' field-with-large-label';
    }
    if (!skipScrollingToField) {
      classes += ' scroll-to-field';
    }
    return cn(classes, classnames);
  }, [largeLabel, skipScrollingToField, classnames]);

  return (
    <div ref={fieldWrapperRef} className={fieldWrapperClassname}>
      <label
        className="label-when-focused-input"
        style={moreDetails ? { pointerEvents: 'initial' } : undefined}
      >
        {label} {moreDetails && <ScreenLink linkData={moreDetails} />}
      </label>
      <div className={cn(styles.buttonsContainer, !inOneRow ? styles.multiLineButtons : {})}>
        {options.map((option) => (
          <SButton
            key={`${name}_${option.value}`}
            {...{ option, handleToggle, onBlur, name, value, theme, disabled }}
          />
        ))}
      </div>
      <div>{error ? <div className="error-text">{error}</div> : null}</div>
      {description ? (
        <div className={styles.note}>
          <small className="muted">{description}</small>
        </div>
      ) : null}
    </div>
  );
};

const emptyFunc = () => {};

function InlineSelectContainer({
  form,
  field,
  sideEffectOnButtonClick = emptyFunc,
  ...rest
}: IButtonSelectProps) {
  const { name } = field;
  const { errors, touched } = form;
  const { theme } = useAppContext();

  const onChange = useCallback(
    async (v: TButtonSelectChange) => {
      form.setFieldValue(name, v);
      await sideEffectOnButtonClick(name, v);
    },
    [form, name, sideEffectOnButtonClick],
  );

  const error = get(errors, name) && get(touched, name) ? get(errors, name) : null;

  return <InlineSelect {...rest} {...field} onChange={onChange} theme={theme} error={error} />;
}

export default InlineSelectContainer;
